import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { database } from '../../Firebase';
import { ref, push, onValue, remove, update } from 'firebase/database';
import { Trash, Pencil, Printer } from 'lucide-react';

const MarriageRegistration = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const { grampanchayatName, arthikVarshLabel } = location.state || {};

  const [view, setView] = useState('list'); // 'list' or 'form'
  const [searchTerm, setSearchTerm] = useState('');
  const [advanceSearch, setAdvanceSearch] = useState(false);
  const [registrations, setRegistrations] = useState([]);

  // Edit State
  const [isEditing, setIsEditing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  // Print State
  const [isPrinting, setIsPrinting] = useState(false);
  const [printData, setPrintData] = useState(null);

  // Form State
  const initialFormState = {
    registrationNo: '',
    registrationDate: new Date().toISOString().split('T')[0],
    marriageDate: '',
    marriagePlace: '',
    groomName: '',
    groomDOB: '',
    groomAge: '',
    groomStatus: '',
    groomAddress: '',
    brideName: '',
    brideDOB: '',
    brideAge: '',
    brideStatus: '',
    brideAddress: '',
    priestName: '',
    witness1Name: '',
    witness2Name: '',
    witness3Name: '',
    remark: ''
  };

  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    const marriageRef = ref(database, 'Grampanchayat/Asthapan/Nondani/Marriage Registration');
    const unsubscribe = onValue(marriageRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const loadRegistrations = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        setRegistrations(loadRegistrations);
      } else {
        setRegistrations([]);
      }
    });

    return () => unsubscribe();
  }, []);

  const calculateAge = (dob) => {
    if (!dob) return '';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
        const newData = { ...prev, [name]: value };
        
        // Auto-calculate age
        if (name === 'groomDOB') {
            newData.groomAge = calculateAge(value);
        }
        if (name === 'brideDOB') {
            newData.brideAge = calculateAge(value);
        }

        return newData;
    });
  };

  const handleSave = () => {
    if (!formData.groomName || !formData.brideName || !formData.registrationNo) {
        alert("Please fill in required fields (Groom Name, Bride Name, Registration No)");
        return;
    }

    if (isEditing && selectedId) {
        const recordRef = ref(database, `Grampanchayat/Asthapan/Nondani/Marriage Registration/${selectedId}`);
        update(recordRef, formData)
          .then(() => {
            alert("Marriage Registration Updated Successfully!");
            resetForm();
          })
          .catch((error) => {
            console.error("Error updating data: ", error);
            alert("Error updating data");
          });
      } else {
        const marriageRef = ref(database, 'Grampanchayat/Asthapan/Nondani/Marriage Registration');
        push(marriageRef, formData)
          .then(() => {
            alert("Marriage Registration Saved Successfully!");
            resetForm();
          })
          .catch((error) => {
            console.error("Error saving data: ", error);
            alert("Error saving data");
          });
      }
  };

  const resetForm = () => {
    setFormData(initialFormState);
    setIsEditing(false);
    setSelectedId(null);
    setView('list');
  };

  const handleEdit = (record) => {
    setFormData(record);
    setSelectedId(record.id);
    setIsEditing(true);
    setView('form');
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      const recordRef = ref(database, `Grampanchayat/Asthapan/Nondani/Marriage Registration/${id}`);
      remove(recordRef)
        .then(() => {
          alert("Record deleted successfully");
        })
        .catch((error) => {
          console.error("Error deleting record: ", error);
          alert("Error deleting record");
        });
    }
  };

  const handlePrint = (record) => {
    setPrintData(record);
    setIsPrinting(true);
    setTimeout(() => {
        window.print();
    }, 500);
  };

  const filteredRegistrations = registrations.filter(reg => 
    reg.groomName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reg.brideName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reg.registrationNo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Printable Certificate Component
  if (isPrinting && printData) {
    return (
        <div className="fixed inset-0 bg-white z-50 p-8 overflow-auto">
             <div className="max-w-3xl mx-auto border-4 border-double border-gray-800 p-8 relative">
                <button 
                    onClick={() => setIsPrinting(false)}
                    className="absolute top-4 right-4 print:hidden px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
                >
                    Close
                </button>

                {/* Certificate Header */}
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">विवाह प्रमाणपत्र (Marriage Certificate)</h2>
                    <h3 className="text-xl font-semibold text-gray-800 mb-1">महाराष्ट्र शासन (Government of Maharashtra)</h3>
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">{grampanchayatName || 'ग्रामपंचायत'}</h3>
                    <p className="text-sm text-gray-600 italic">
                        (महाराष्ट्र विवाह नोंदणी अधिनियम, 1998 अन्वये देण्यात आले आहे.)
                    </p>
                    <p className="text-sm text-gray-600 italic mt-1">
                        (Issued under Maharashtra Regulation of Marriage Bureaus and Registration of Marriages Act, 1998.)
                    </p>
                </div>

                {/* Certificate Body */}
                <div className="mb-8 leading-relaxed text-gray-800">
                    <p className="mb-4">
                        प्रमाणित करण्यात येते की, खालील माहिती विवाह नोंदवहीच्या मूळ नोंदीवरून घेण्यात आली आहे, जी 
                        <span className="font-bold mx-2">{grampanchayatName || '___'}</span> 
                        तालुका <span className="font-bold mx-2">___</span> 
                        जिल्हा <span className="font-bold mx-2">___</span> 
                        राज्याच्या विवाह नोंदवहीत उल्लेख आहे.
                    </p>

                    <div className="grid grid-cols-2 gap-y-4 gap-x-8 text-lg">
                        <div className="col-span-2">
                             <h4 className="font-bold underline mb-2">वराची माहिती (Groom's Details)</h4>
                        </div>
                        <div>
                            <span className="font-semibold block text-sm text-gray-600">नाव (Name):</span>
                            <span className="font-bold">{printData.groomName}</span>
                        </div>
                         <div>
                            <span className="font-semibold block text-sm text-gray-600">वय (Age):</span>
                            <span>{printData.groomAge}</span>
                        </div>
                        <div className="col-span-2">
                            <span className="font-semibold block text-sm text-gray-600">पत्ता (Address):</span>
                            <span>{printData.groomAddress}</span>
                        </div>

                        <div className="col-span-2 mt-4">
                             <h4 className="font-bold underline mb-2">वधूची माहिती (Bride's Details)</h4>
                        </div>
                        <div>
                            <span className="font-semibold block text-sm text-gray-600">नाव (Name):</span>
                            <span className="font-bold">{printData.brideName}</span>
                        </div>
                         <div>
                            <span className="font-semibold block text-sm text-gray-600">वय (Age):</span>
                            <span>{printData.brideAge}</span>
                        </div>
                        <div className="col-span-2">
                            <span className="font-semibold block text-sm text-gray-600">पत्ता (Address):</span>
                            <span>{printData.brideAddress}</span>
                        </div>

                         <div className="col-span-2 mt-4 border-t pt-4">
                             <h4 className="font-bold underline mb-2">विवाह तपशील (Marriage Details)</h4>
                        </div>
                        <div>
                            <span className="font-semibold block text-sm text-gray-600">विवाह दिनांक (Date of Marriage):</span>
                            <span className="font-bold">{printData.marriageDate}</span>
                        </div>
                        <div>
                            <span className="font-semibold block text-sm text-gray-600">विवाह ठिकाण (Place of Marriage):</span>
                            <span>{printData.marriagePlace}</span>
                        </div>
                         <div>
                            <span className="font-semibold block text-sm text-gray-600">नोंदणी क्रमांक (Registration No):</span>
                            <span>{printData.registrationNo}</span>
                        </div>
                         <div>
                            <span className="font-semibold block text-sm text-gray-600">नोंदणी दिनांक (Date of Registration):</span>
                            <span>{printData.registrationDate}</span>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-16 pt-8 flex justify-between items-end">
                    <div className="text-center">
                        <p className="border-t border-gray-400 px-4 pt-2">दिनांक (Date)</p>
                    </div>
                    <div className="text-center">
                         <div className="h-16 mb-2"></div> 
                        <p className="border-t border-gray-400 px-4 pt-2 font-bold">विवाह निबंधक</p>
                        <p className="text-sm">Marriage Registrar</p>
                        <p className="font-bold">{grampanchayatName}</p>
                    </div>
                </div>

             </div>
        </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8 font-poppins">
      
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="bg-[#267c77] rounded-lg shadow-sm p-4 flex justify-between items-center text-white">
            <div>
                 <h1 className="text-xl font-bold">
                    विवाह नोंदणी यादी {isEditing ? '(संपादन सुरू)' : ''}
                 </h1>
            </div>
            <div className="flex gap-2">
                 <button 
                   onClick={() => navigate('/asthapan/registrations', { state: location.state })}
                   className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition"
                 >
                   मागे जा
                 </button>
                 {view === 'list' && (
                   <button 
                     onClick={() => {
                        resetForm();
                        setView('form');
                     }}
                     className="px-4 py-2 bg-[#267c77] text-white rounded hover:bg-[#1e6b66] transition"
                   >
                     नविन
                   </button>
                 )}
            </div>
        </div>
      </div>

      {view === 'list' ? (
        /* LIST VIEW */
        <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
          
          {/* Search Bar */}
          <div className="p-4 border-b border-gray-200">
             <div className="bg-[#267c77] p-2 rounded-t text-white flex items-center gap-2 mb-2">
                <input 
                    type="checkbox" 
                    id="advanceSearch"
                    checked={advanceSearch}
                    onChange={(e) => setAdvanceSearch(e.target.checked)}
                    className="w-4 h-4"
                />
                <label htmlFor="advanceSearch" className="text-black font-medium">अधिक शोध (Advance Search)</label>
             </div>
             <div className="flex gap-4">
                <div className="relative flex-1">
                    <input 
                    type="text" 
                    placeholder="नोंदणी नं./ वराचे नाव / वधूचे नाव" 
                    className="w-full px-4 py-2 border border-[#267c77]/30 rounded focus:outline-none focus:ring-2 focus:ring-[#267c77]"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <button className="px-6 py-2 bg-[#267c77] text-white rounded hover:bg-[#1e6b66] transition">
                    शोधणे
                </button>
             </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#267c77] text-white">
                  <th className="p-3 border-r border-white/30 font-medium text-center">नोंदणीनं.</th>
                  <th className="p-3 border-r border-white/30 font-medium text-center">नोंदणी दिनांक</th>
                  <th className="p-3 border-r border-white/30 font-medium text-center">विवाह दिनांक</th>
                  <th className="p-3 border-r border-white/30 font-medium">वराचे नाव</th>
                  <th className="p-3 border-r border-white/30 font-medium">वधूचे नाव</th>
                  <th className="p-3 font-medium text-center">पर्याय</th>
                </tr>
              </thead>
              <tbody>
                  {filteredRegistrations.length > 0 ? (
                    filteredRegistrations.map((reg, index) => (
                        <tr key={reg.id} className="border-b hover:bg-gray-50">
                            <td className="p-3 text-center border-r">{reg.registrationNo}</td>
                            <td className="p-3 text-center border-r">{reg.registrationDate}</td>
                            <td className="p-3 text-center border-r">{reg.marriageDate}</td>
                            <td className="p-3 border-r">{reg.groomName}</td>
                            <td className="p-3 border-r">{reg.brideName}</td>
                            <td className="p-3 text-center flex justify-center gap-2">
                                <button 
                                    onClick={() => handleEdit(reg)}
                                    className="p-1.5 bg-[#267c77] text-white rounded hover:bg-[#1e6b66]"
                                    title="Edit"
                                >
                                    <Pencil size={18} />
                                </button>
                                <button 
                                    onClick={() => handlePrint(reg)}
                                    className="p-1.5 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                                    title="Print Certificate"
                                >
                                    <Printer size={18} />
                                </button>
                                <button 
                                    onClick={() => handleDelete(reg.id)}
                                    className="p-1.5 bg-red-500 text-white rounded hover:bg-red-600"
                                    title="Delete"
                                >
                                    <Trash size={18} />
                                </button>
                            </td>
                        </tr>
                    ))
                  ) : (
                    <tr>
                        <td colSpan="6" className="p-4 text-center text-gray-500 bg-white">
                            No records found
                        </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>

           {/* Pagination */}
           <div className="p-2 border-t bg-[#267c77] text-white flex justify-center items-center gap-2">
                <button className="px-2 py-1 bg-white/20 rounded hover:bg-white/30 text-sm">|&lt;</button>
                <button className="px-2 py-1 bg-white/20 rounded hover:bg-white/30 text-sm">&lt;&lt;</button>
                <span className="px-3 py-1 bg-white/20 rounded text-sm bg-[#1e6b66]">1</span>
                <button className="px-2 py-1 bg-white/20 rounded hover:bg-white/30 text-sm">&gt;&gt;</button>
                <button className="px-2 py-1 bg-white/20 rounded hover:bg-white/30 text-sm">&gt;|</button>
                <select className="ml-2 bg-white text-black text-sm p-1 rounded">
                    <option>5</option>
                    <option>10</option>
                    <option>20</option>
                </select>
           </div>
        </div>
      ) : (
        /* FORM VIEW */
        <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-md p-6 border-t-4 border-[#267c77]">
             <div className="mb-6 border-b pb-2">
                 <h2 className="text-xl font-medium text-white bg-[#267c77] inline-block px-4 py-1 rounded-t-lg w-full">
                    {isEditing ? 'विवाहाची माहिती संपादित करा' : 'विवाहाची माहिती'}
                 </h2>
             </div>
            <div className="space-y-6">
                
                {/* Registration Details */}
                <h3 className="font-semibold text-[#267c77] border-b pb-1">नोंदणी तपशील</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">नोंदणी क्रमांक</label>
                        <input type="text" name="registrationNo" value={formData.registrationNo} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                    <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">नोंदणी दिनांक</label>
                        <input type="date" name="registrationDate" value={formData.registrationDate} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                </div>

                {/* Marriage Details */}
                <h3 className="font-semibold text-[#267c77] border-b pb-1">विवाह तपशील</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">विवाह दिनांक</label>
                        <input type="date" name="marriageDate" value={formData.marriageDate} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                    <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">विवाह ठिकाण</label>
                        <input type="text" name="marriagePlace" value={formData.marriagePlace} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                </div>

                {/* Groom's Details */}
                <h3 className="font-semibold text-[#267c77] border-b pb-1">वराची माहिती</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                     <div className="md:col-span-2 lg:col-span-4">
                        <label className="block text-gray-700 text-sm font-medium mb-1">पूर्ण नाव</label>
                        <input type="text" name="groomName" value={formData.groomName} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                    <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">जन्म दिनांक</label>
                        <input type="date" name="groomDOB" value={formData.groomDOB} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                    <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">वय</label>
                        <input type="number" name="groomAge" value={formData.groomAge} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                     <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">विवाहापूर्वीची स्थिती</label>
                        <select name="groomStatus" value={formData.groomStatus} onChange={handleInputChange} className="w-full p-2 border rounded">
                            <option value="">निवडा</option>
                            <option value="Unmarried">अविवाहित</option>
                            <option value="Divorced">घटस्फोटित</option>
                            <option value="Widower">विदुर</option>
                        </select>
                    </div>
                     <div className="md:col-span-2 lg:col-span-4">
                        <label className="block text-gray-700 text-sm font-medium mb-1">पत्ता</label>
                        <textarea name="groomAddress" value={formData.groomAddress} onChange={handleInputChange} className="w-full p-2 border rounded" rows="2"></textarea>
                    </div>
                </div>

                 {/* Bride's Details */}
                 <h3 className="font-semibold text-[#267c77] border-b pb-1">वधूची माहिती</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                     <div className="md:col-span-2 lg:col-span-4">
                        <label className="block text-gray-700 text-sm font-medium mb-1">पूर्ण नाव</label>
                        <input type="text" name="brideName" value={formData.brideName} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                    <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">जन्म दिनांक</label>
                        <input type="date" name="brideDOB" value={formData.brideDOB} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                    <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">वय</label>
                        <input type="number" name="brideAge" value={formData.brideAge} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                     <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">विवाहापूर्वीची स्थिती</label>
                        <select name="brideStatus" value={formData.brideStatus} onChange={handleInputChange} className="w-full p-2 border rounded">
                            <option value="">निवडा</option>
                            <option value="Unmarried">अविवाहित</option>
                            <option value="Divorced">घटस्फोटित</option>
                            <option value="Widow">विधवा</option>
                        </select>
                    </div>
                     <div className="md:col-span-2 lg:col-span-4">
                        <label className="block text-gray-700 text-sm font-medium mb-1">पत्ता</label>
                        <textarea name="brideAddress" value={formData.brideAddress} onChange={handleInputChange} className="w-full p-2 border rounded" rows="2"></textarea>
                    </div>
                </div>

                {/* Priest and Witnesses */}
                <h3 className="font-semibold text-[#267c77] border-b pb-1">भटजी व साक्षीदार</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div className="md:col-span-2">
                        <label className="block text-gray-700 text-sm font-medium mb-1">भटजीचे नाव</label>
                        <input type="text" name="priestName" value={formData.priestName} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                     <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">साक्षीदार १ चे नाव</label>
                        <input type="text" name="witness1Name" value={formData.witness1Name} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                     <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">साक्षीदार २ चे नाव</label>
                        <input type="text" name="witness2Name" value={formData.witness2Name} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                     <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">साक्षीदार ३ चे नाव</label>
                        <input type="text" name="witness3Name" value={formData.witness3Name} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                </div>

            </div>

             {/* Footer Buttons */}
             <div className="mt-8 flex justify-center gap-4 py-4 bg-[#267c77] rounded-b-lg -mx-6 -mb-6">
                <button 
                  onClick={handleSave}
                  className="px-6 py-2 bg-[#267c77] text-white rounded hover:bg-[#1e6b66] transition font-medium"
                >
                    {isEditing ? 'अपडेट करा' : 'साठविणे'}
                </button>
                <button 
                  onClick={() => setView('list')}
                  className="px-6 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition font-medium"
                >
                    रद्द करणे
                </button>
             </div>
        </div>
      )}

    </div>
  );
};

export default MarriageRegistration;
